import * as React from 'react';
import { CodeConfirmationModel } from '../../models/user/user';
import { authenticationService } from '../../services/authentication';
import { Link } from 'react-router-dom';
import { MAX_EMAIL_STRING_LENGTH, UNCONFIRMED_USER_SESSION_STORAGE_KEY } from '../../utils/constants';
import { PageRoutes } from '../root/Routes';
import VerificationCodeComponent from './VerificationCodeComponent';
import { extractQueryParameters, QueryParameters, tryQueryParameterCodeConfirmation } from './authenticationUtility';
import { LogManager } from '../../utils/LogManager';
import { Event } from 'fs-api-client';
import { VERIFICATION_MESSAGE } from '../../../backoffice/utils/constants';
import { renderWaitingForSuccessElement } from '../common/Icons';
import './authentication.css';
import { CardHeader } from '../common/authenticationCardComponents/CardHeader';
import { LucidLinkLogo } from '../common/Icons';
import { LUCIDLINK_WEBSITE } from '../../utils/constants';
import { pushEventToAnalytics, splitEmailToComponents } from '../../utils/utilFunctions';

const logManager = new LogManager('CodeConfirmation');

interface CodeConfirmationState {
    unconfirmedUser: string;
    validation: {
        codeExpired: boolean;
        wrongCode: boolean;
        limitExceeded: boolean;
        failedToDeliver: boolean;
    };
    verifyingCode: boolean;
    hasError: boolean;
    codeAlreadyConfirmed: boolean;
    sendAgainMessage: string;
    code: number[];
}

const DEFAULT_VALIDATION = {
    codeExpired: false,
    wrongCode: false,
    limitExceeded: false,
    failedToDeliver: false
};

class CodeConfirmation extends React.Component < any, CodeConfirmationState > {
    clearCode: () => void;

    constructor(props: any) {
        super(props);

        this.checkReadyToSend = this.checkReadyToSend.bind(this);
        this.determineContentMessage = this.determineContentMessage.bind(this);
        this.determineFooterMessage = this.determineFooterMessage.bind(this);
        this.confirmCode = this.confirmCode.bind(this);
        this.handleSendAgainClick = this.handleSendAgainClick.bind(this);
        this.resetStateWhileVerifyingCode = this.resetStateWhileVerifyingCode.bind(this);

        const queryParameters: QueryParameters = extractQueryParameters(this.props.location);
        tryQueryParameterCodeConfirmation(queryParameters, this.confirmCode);

        const unconfirmedUser = queryParameters.user ||
            this.props.history?.location?.props?.unconfirmedUser ||
            localStorage.getItem(UNCONFIRMED_USER_SESSION_STORAGE_KEY);

        this.state = {
            unconfirmedUser,
            validation: Object.assign({}, DEFAULT_VALIDATION),
            codeAlreadyConfirmed: false,
            sendAgainMessage: '',
            hasError: false,
            code: new Array(6),
            verifyingCode: false
        };
    }

    componentDidMount() {
        const queryParameters: QueryParameters = extractQueryParameters(this.props.location);
        if (queryParameters.user && queryParameters.code?.length === 6) {
            this.resetStateWhileVerifyingCode();
        }
        if (!this.state.unconfirmedUser || this.props.match.params.user) {
            this.props.history.push(PageRoutes.Register);
            return;
        }
        this.setState({ validation: Object.assign({}, DEFAULT_VALIDATION) });
    }

    render() {
        let currentEmail = this.state.unconfirmedUser || '';
        let { emailName, emailDomainFull, emailProviderName } = splitEmailToComponents(currentEmail);

        if (emailName.length > MAX_EMAIL_STRING_LENGTH) {
            emailName = emailName.substring(0, MAX_EMAIL_STRING_LENGTH - 3) + '...';
        }
        if (emailProviderName.length > MAX_EMAIL_STRING_LENGTH) {
            emailProviderName = emailProviderName.substring(0, MAX_EMAIL_STRING_LENGTH - 3) + '...';
        }
        currentEmail = emailName + '@' + emailProviderName + '.' + emailDomainFull[emailDomainFull.length - 1];

        const contentMessage = this.determineContentMessage();
        const defaultCardMessage = this.defaultCardMessage(currentEmail);
        const sendAgainMessageComponent = <strong> {this.state.sendAgainMessage} </strong>;

        return (
            <>
            <div className="auth">
                <div className="auth_wrapper">
                    <header>
                        <a href={LUCIDLINK_WEBSITE}>
                            <LucidLinkLogo width={'130px'} height={'50px'}/>
                        </a>
                    </header>
                    <div className="auth_block">
                        <div>
                            <CardHeader message={'Verify your email'} verifyEmail={defaultCardMessage}/>
                            <div className="auth_form center">
                                {this.state.verifyingCode && renderWaitingForSuccessElement(VERIFICATION_MESSAGE)}
                                    {!this.state.verifyingCode && !this.state.codeAlreadyConfirmed &&
                                        <div className="form">
                                            <div className="float-label-control no__animation six-digits ml-0 mr-0">
                                                <VerificationCodeComponent
                                                    setClearCode={(clearCode => {
                                                        this.clearCode = clearCode;
                                                    })}
                                                    onCodeEntered={this.checkReadyToSend}
                                                />
                                                <label>
                                                    Enter your verification code
                                                </label>
                                            </div>
                                            {contentMessage}
                                            {this.state.codeAlreadyConfirmed ? null : sendAgainMessageComponent}
                                        </div>}
                                    {this.state.codeAlreadyConfirmed &&
                                        <div className="flex-center">
                                            <h3>{currentEmail}<br/>is already confirmed.</h3>
                                            <Link className="btn btn-middle" to="/login">Sign in</Link>
                                        </div>
                                    }
                            </div>
                            {this.determineFooterMessage()}
                        </div>
                    </div>
                </div>
            </div>
            </>
        );
    }

    private checkReadyToSend = (code: number[]) => {
        this.confirmCode(this.state.unconfirmedUser, code);
    }

    private determineFooterMessage(): JSX.Element {
        if (!this.state.codeAlreadyConfirmed) {
            return (
                <footer className="center">
                    <p className="mb-0">Haven't received an email?</p>
                    <p className="mb-0">Check your spam folder or <a href="#" onClick={this.handleSendAgainClick}>Send again</a></p>
                </footer>
            );
        }

        return (
            <footer className="center b40">&nbsp;</footer>
        );
    }

    private determineContentMessage(): JSX.Element {
        if (this.state.codeAlreadyConfirmed) {
            return <></>;
        }
        if (this.state.validation.failedToDeliver) {
            return (
                <div className="center">
                    <p className="mb-0 red">We failed to deliver your confirmation code.</p>
                    <p className="mb-0">Please request a new one by clicking the send again button or <Link to={'/register'}>register</Link> again.</p>
                </div>
            );
        }
        if (this.state.validation.wrongCode) {
            return (
                <div className="center">
                    <p className="mb-0 red">The verification code is incorrect.</p>
                    <p className="mb-0">Check your email for the verification code and try again.</p>
                </div>
            );
        }
        if (this.state.validation.codeExpired) {
            return (
                <div className="center">
                    <p className="mb-0 red">Your verification code has expired.</p>
                    <p className="mb-0">Please request a new one by clicking the send again button.</p>
                </div>
            );
        }
        if (this.state.hasError) {
            return (
                <div className="center">
                    <p className="mb-0 red">Something went wrong.</p>
                    <p className="mb-0">Please try again later.</p>
                </div>
            );
        }

        return <></>;
    }

    private async handleSendAgainClick(e: React.MouseEvent<HTMLAnchorElement>): Promise<void> {
        e.preventDefault();
        logManager.log(
            `"Send again" link clicked for email: ${this.state.unconfirmedUser}`,
            true,
            Event.EventTypeEnum.Signups
        );
        try {
            this.resetStateWhileVerifyingCode();
            await authenticationService.resendCode(this.state.unconfirmedUser);
            await this.onResendSuccess();
        } catch (e) {
            this.onResendError(e);
            this.setState({ verifyingCode: false });
        }
    }

    private resetStateWhileVerifyingCode() {
        this.setState({
            verifyingCode: true,
            hasError: false,
            validation: {
                codeExpired: false,
                wrongCode: false,
                limitExceeded: false,
                failedToDeliver: false
            },
            sendAgainMessage: '',
            codeAlreadyConfirmed: false
        });
    }

    private onResendSuccess = () => {
        let validation = Object.assign({}, this.state.validation);
        validation.wrongCode = false;
        validation.codeExpired = false;
        this.setState(
            {
                sendAgainMessage: 'The code has been re-sent to your email.',
                validation: validation,
                verifyingCode: false
            });
        this.clearCode();
    }

    private onResendError = (err: any) => {
        let validation = Object.assign({}, this.state.validation);
        validation.wrongCode = false;
        if (err.message === 'User is already confirmed.') {
            this.setState({ codeAlreadyConfirmed: true, validation: validation });
            return;
        }
        if (err.code === 'LimitExceededException') {
            validation.limitExceeded = true;
            this.setState({ validation: validation, sendAgainMessage: 'Unable to resend code due to too many requests. Please try again later.' });
            return;
        }
        if (err.code === 'CodeDeliveryFailureException') {
            validation.failedToDeliver = false;
            this.setState({ sendAgainMessage: 'We failed to deliver your confirmation code. Please try again later or sign up with a different email.', validation: validation });
            return;
        }
        this.setState({ sendAgainMessage: 'Unable to resend code. Please try again later or sign up with a different email.', validation: validation });
        logManager.error(err);
    }

    private async confirmCode(user: string, codeDigits: number[]) {
        let codeStr: string = '';

        for (let i = 0; i < codeDigits.length; i++) {
            const codeDigit: number = codeDigits[i];
            codeStr = codeStr + codeDigit.toString();
        }

        let codeConfirmation = new CodeConfirmationModel();
        codeConfirmation.email = user;
        codeConfirmation.code = codeStr;
        try {
            this.resetStateWhileVerifyingCode();
            const result = await authenticationService.confirmCode(codeConfirmation);
            await this.onConfirmationSuccess(result, user);
            this.setState({ verifyingCode: false });
        } catch (e) {
            this.setState({ verifyingCode: false });
            this.onConfirmationError(e);
        }
    }

    private onConfirmationSuccess = async (result: any, email: string): Promise<void> => {
        if (result !== 'SUCCESS') {
            return;
        }
        if (authenticationService.getWasAuthenticated()) {
            // It is possible to be logged in, while confirming verfication code
            // This can happen if you register through the website for example
            // We have to log out the current user, so that we can redirect to login page
            authenticationService.signOut();
            return;
        }
        const sourceBytes = new TextEncoder().encode(email);
        const digest = await window.crypto.subtle.digest('SHA-256', sourceBytes);
        const resultBytes = Array.from(new Uint8Array(digest));
        const hashedEmail = resultBytes.map(x => x.toString(16).padStart(2, '0')).join('');
        pushEventToAnalytics('account_creation_confirmed', {customerEmail: hashedEmail});
        this.props.history.push({
            pathname: PageRoutes.Login,
            props: { message: 'Your code has been confirmed. You can log in with your account', email: email },
            search: `?confirmedEmail=${encodeURIComponent(email)}`
        });
    }

    private onConfirmationError = (err: any) => {
        let validation = Object.assign({}, DEFAULT_VALIDATION);
        logManager.error(err);
        if (err.message === 'User cannot confirm because user status is not UNCONFIRMED.') {
            this.setState({ codeAlreadyConfirmed: true });
            return;
        }
        if (err.message === 'User cannot be confirmed. Current status is CONFIRMED') {
            this.setState({ codeAlreadyConfirmed: true });
            return;
        }
        switch (err.code) {
            case 'CodeMismatchException':
                validation.wrongCode = true;
                this.setState({ validation });
                break;
            case 'UserNotFoundException':
                validation.codeExpired = true;
                this.setState({ validation });
                break;
            case 'ExpiredCodeException':
                validation.codeExpired = true;
                this.setState({ validation });
                break;
            case 'LimitExceededException':
                validation.limitExceeded = true;
                this.setState({ validation, sendAgainMessage: 'Unable to confirm code due to too many requests. Please try again later.' });
                break;
            default:
                this.setState({ hasError: true });
                break;
        }
    }

    private defaultCardMessage(currentEmail: string): JSX.Element {
        return (
            <div className="center">
                <p className="mb-0">A verification code has been sent to</p>
                <p className="mb-0"><strong>{currentEmail}</strong></p>
            </div>
        );
    }
}

export default CodeConfirmation;



// WEBPACK FOOTER //
// ./src/webportal/components/authentication/CodeConfirmation.tsx